/*
 * Copyright (c) Fing Ltd. All rights reserved.
 */

import React, { Component } from 'react';

import {Link, Redirect, withRouter} from "react-router-dom";

import AppNavbar from "./../components/AppNavbar";
import AppFooter from "../components/AppFooter";
import {isUserProfileLoaded, isUserSignedIn, UserContext} from "../UserContext";

import {
    isFreeProduct,
    intervalIdFromParams,
    promoCodeFromParams, productIdFromParams, discountOfferFromParams
} from "../helpers/BillingHelper";


import { loadStripe } from "@stripe/stripe-js";
import {EmbeddedCheckout, EmbeddedCheckoutProvider} from "@stripe/react-stripe-js";
import WaitingComponent from "../components/WaitingComponent";
import Loader from "react-loader-spinner";

// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
let stripePromise = null;

class SubscribeCheckout extends Component {

    static contextType = UserContext;

    constructor(props) {
        super(props);
        this.state = {
            clientSecret: null,
            serverError: null
        };
    }

    componentDidMount() {
        const query = new URLSearchParams(this.props.location.search);
        if (query.has("session_id")) {
            this.retrieveCheckoutSession(query.get("session_id"));
        } else {
            this.createCheckoutSession(query);
        }

        window.scrollTo(0, 0)
        document.title = "Fing - Checkout";
    }

    retrieveCheckoutSession(sessionId) {
        fetch(`/rest/subscription/verify?session_id=${sessionId}`)
            .then(response => this.validateRestResponse(response))
            .then(response => response.json())
            .then(result => {
                this.setState({clientSecret: result.clientSecret});
            })
            .catch(e => {
                this.catchRestError(e);
                this.setState({serverError: true});
            });
    }

    createCheckoutSession(query) {
        const productId = productIdFromParams(query);
        const intervalId = intervalIdFromParams(query);
        const promoCode = promoCodeFromParams(query);
        const discountOffer = discountOfferFromParams(query);

        // From state
        const checkoutParams = new URLSearchParams();
        if (productId) checkoutParams.set("product", productId.toLowerCase());
        if (intervalId) checkoutParams.set("interval", intervalId.toLowerCase());
        if (promoCode) checkoutParams.set("promo_code", promoCode);
        if (discountOffer) checkoutParams.set("discount_offer", discountOffer);

        fetch(`/rest/subscription/checkout?${checkoutParams.toString()}`)
            .then(response => this.validateRestResponse(response))
            .then(response => response.json())
            .then(result => {
                this.setState({clientSecret: result.clientSecret});
            })
            .catch(e => {
                this.catchRestError(e);
                this.setState({serverError: true});
            });
    }

    validateRestResponse(response) {
        if (response.status !== 200) {
            throw Error(response.statusText);
        }
        return response;
    }

    catchRestError(response) {
        console.log("Failed to fetch plans: " + response);
    }

    // --------------------------------------------------------------------------------

    render() {
        return (
            <div style={{backgroundColor: "#f8f8f8"}}>
                <AppNavbar mode='account' fillBackground="false" />
                <main id="content" role="main">
                    {this.renderMainContent()}
                </main>
                <AppFooter />
            </div>
        );
    }

    renderMainContent() {
        return (
            <UserContext.Consumer>
                {userContext => this.renderMainContentInContext(userContext)}
            </UserContext.Consumer>
        )
    }

    renderMainContentInContext(userContext) {
        const params = new URLSearchParams(this.props.location.search);
        const productId = productIdFromParams(params);

        if (!stripePromise && userContext.stripePublicApiKey) {
            stripePromise = loadStripe(userContext.stripePublicApiKey);
        }

        if (isUserSignedIn(userContext) && !isFreeProduct(productId)) {
            return this.renderPurchasePlan(userContext.userProfile);
        } else if (isUserProfileLoaded(userContext)) {
            return (<Redirect to={`/account/subscribe/cart?${params.toString()}`} />);
        }
    }

    renderPurchasePlan(userProfile) {
        const {clientSecret, serverError} = this.state;
        const PageTitle = () => <>
            <h1 className="h2 font-weight-bolder mb-0">Review your order</h1>
            <p className="lead mb-6"><span className="fa fa-fw fa-lock small" /> Your payment is 100% secure and encrypted. Cancel anytime.</p>
        </>;

        if (serverError) {
            const params = new URLSearchParams(this.props.location.search);
            return <section className="container space-1">
                <PageTitle />
                <p className="my-4 w-md-50 mx-auto alert alert-soft-danger p-6">
                    We encountered an issue while attempting to start your checkout session.
                    This could be due to network connectivity or invalid payment information or a technical issue on our side.
                </p>
                <div className="max-w-50rem mx-auto mt-10">
                    <Link className="btn btn-primary btn-sm w-100"
                       href={`/account/subscribe/cart?${params.toString()}`}>Try again</Link>
                </div>
            </section>
        }

        if (!clientSecret) {
            return <section className="container space-1">
                <PageTitle />
                <div className="d-lg-flex align-items-center justify-content-center py-11">
                    <Loader
                        type="Rings"
                        color="#1a6aff"
                        secondaryColor="#1a6aff"
                        height={100}
                        width={100}
                    />
                </div>
            </section>;
        }

        const checkoutOptions = {clientSecret: clientSecret};
        return <section className="container space-1">
            <PageTitle />
            <div id="checkout">
                <EmbeddedCheckoutProvider
                    stripe={stripePromise}
                    options={checkoutOptions}
                >
                    <EmbeddedCheckout />
                </EmbeddedCheckoutProvider>
            </div>
        </section>;
    }


}

export default withRouter(SubscribeCheckout)